import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import apiService from '../services/api';
import apiWithFallback from '../utils/apiWithFallback';
import toast from 'react-hot-toast';

const CompanySettingsContext = createContext();

export const useCompanySettings = () => {
  const context = useContext(CompanySettingsContext);
  if (!context) {
    throw new Error('useCompanySettings must be used within a CompanySettingsProvider');
  }
  return context;
};

export const CompanySettingsProvider = ({ children }) => {
  const [companySettings, setCompanySettings] = useState({
    name: '',
    logo: '',
    address: '',
    city: '',
    wilaya: '',
    postalCode: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    taxId: '',
    rc: '',
    nif: '',
    nis: '',
    art: ''
  });
  const [loading, setLoading] = useState(true);

  // Load company settings
  useEffect(() => {
    const loadCompanySettings = async () => {
      try {
        // First try to load from localStorage for immediate display
        const localSettings = localStorage.getItem('stockpro_companySettings');
        if (localSettings) {
          setCompanySettings(JSON.parse(localSettings));
        }
        
        // Then try to get from API
        const settings = await apiWithFallback.getData(
          'companySettings', 
          apiService.getCompanySettings, 
          'stockpro_companySettings'
        );
        
        if (settings) {
          // Transform API response to match our state structure
          const transformedSettings = {
            name: settings.name || '',
            logo: settings.logo || '',
            address: settings.address || '',
            city: settings.city || '',
            wilaya: settings.wilaya || '',
            postalCode: settings.postal_code || '',
            country: settings.country || '',
            phone: settings.phone || '',
            email: settings.email || '',
            website: settings.website || '',
            taxId: settings.tax_id || '',
            rc: settings.rc || '',
            nif: settings.nif || '',
            nis: settings.nis || '',
            art: settings.art || ''
          };
          
          setCompanySettings(transformedSettings);
          localStorage.setItem('stockpro_companySettings', JSON.stringify(transformedSettings));
        }
      } catch (error) {
        console.error('Error loading company settings:', error);
        toast.error('Erreur lors du chargement des paramètres de l\'entreprise');
      } finally {
        setLoading(false);
      }
    };

    loadCompanySettings();
  }, []);

  // Update company settings
  const updateCompanySettings = async (newSettings) => {
    try {
      setLoading(true);
      
      // Transform to API format
      const apiSettings = {
        name: newSettings.name,
        logo: newSettings.logo,
        address: newSettings.address,
        city: newSettings.city,
        wilaya: newSettings.wilaya,
        postal_code: newSettings.postalCode,
        country: newSettings.country,
        phone: newSettings.phone,
        email: newSettings.email,
        website: newSettings.website,
        tax_id: newSettings.taxId,
        rc: newSettings.rc,
        nif: newSettings.nif,
        nis: newSettings.nis,
        art: newSettings.art
      };
      
      // Update in state and localStorage immediately for responsive UI
      setCompanySettings(newSettings);
      localStorage.setItem('stockpro_companySettings', JSON.stringify(newSettings));
      
      // Then update API
      try {
        await apiService.updateCompanySettings(apiSettings);
        toast.success('Paramètres de l\'entreprise mis à jour avec succès');
      } catch (error) {
        console.error('API error updating company settings:', error);
        // We already updated localStorage, so just show a message about server sync
        toast.error('Les paramètres ont été enregistrés localement, mais la synchronisation avec le serveur a échoué');
      }
      
      return true;
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast.error('Erreur lors de la mise à jour des paramètres de l\'entreprise');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompanySettingsContext.Provider
      value={{
        companySettings,
        updateCompanySettings,
        loading
      }}
    >
      {children}
    </CompanySettingsContext.Provider>
  );
};